/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "@padi/ui";
import type { PaginationProps } from "../lib/types";
import React from "react";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useSignOut } from "@padi/lib/hooks/use-auth";

interface StateConfig {
  text?: string;
  className?: string;
}

interface DataStateProps<T> {
  query: {
    data: T | undefined;
    isLoading: boolean;
    error?: Error | null;
    isError?: boolean;
    pagination?: PaginationProps;
    refetch?: () => void;
  };
  isEmpty?: (data?: T) => boolean;
  loading?: StateConfig | React.ReactNode;
  errorTitle?: string;
  empty?: StateConfig | React.ReactNode;
  children: (data: T, pagination?: PaginationProps) => React.ReactNode;
}

type ErrorDisplayProps = {
  error?: Error | null;
  title?: string;
  message?: string;
  isLoading?: boolean;
  refetch?: () => void;
  className?: string;
};

export function ErrorDisplay({
  error,
  title = "Error fetching data",
  message,
  isLoading,
  refetch,
  // className,
}: ErrorDisplayProps) {
  const errorMessage =
    message || error?.message || "An unexpected error occurred";

  return (
    <div className="min-h-full flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-destructive" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground mb-6">{errorMessage}</p>
        {refetch && (
          <Button
            onClick={() => {
              toast.info("Retrying...");
              refetch();
            }}
            variant="outline"
            size="sm"
            loading={isLoading}
          >
            Try again
          </Button>
        )}
      </div>
    </div>
  );
}

// Default components
const defaultStates = {
  loading: () => <div className="h-10 w-full animate-pulse bg-muted rounded" />,
  empty: (text = "No data available") => (
    <div className="text-sm text-muted-foreground text-center h-full max-h-32 flex items-center justify-center">
      {text}
    </div>
  ),
};

function createStateComponent(
  config: StateConfig | React.ReactNode | undefined,
  defaultComponent: (text?: string, refetch?: () => void) => React.ReactNode,
): React.ReactNode {
  if (React.isValidElement(config)) return config;
  if (!config) return defaultComponent();

  if (typeof config === "string" || typeof config === "number") {
    return defaultComponent(String(config));
  }

  const { text, className } = config as StateConfig;

  if (className && text) {
    return <div className={className}>{text}</div>;
  }

  if (text) {
    return defaultComponent(text);
  }

  return defaultComponent();
}

export function DataState<T>({
  query,
  loading,
  errorTitle,
  empty,
  children,
}: DataStateProps<T>) {
  const { signOut } = useSignOut();
  const { isLoading, data, error, isError, pagination, refetch } = query;
  if (isError) {
    const isAuthError = (error as any)?.code === "UNAUTHORIZED";

    if (isAuthError) {
      signOut();
      return null;
    }
    return (
      <ErrorDisplay
        title={errorTitle}
        error={error}
        isLoading={isLoading}
        refetch={refetch}
      />
    );
  }
  if (isLoading) {
    return <>{createStateComponent(loading, defaultStates.loading)}</>;
  }

  if (!data || isEmpty(data)) {
    return <>{createStateComponent(empty, defaultStates.empty)}</>;
  }

  return <>{children(data, pagination)}</>;
}

function isEmpty(d: any) {
  return (
    !d ||
    (Array.isArray(d) && d.length === 0) ||
    (d.constructor === Object && Object.keys(d).length === 0) ||
    (d as any).length === 0
  );
}
